import { MARKING_SEGMENTS_MAX, MARKING_TEXT_MAX } from '#shared/utils/markings/schema'
import { MARKING_MESSAGES } from '~/constants/markings'
import { captureSelectionContext, computeMarkableOffsetWithin } from './selection'
import { captureMarkingSource } from './source'
import { findCitationParent, iterateMarkableTextNodes, iterateOwnMarkableTextNodes } from './text-filter'

export class MarkingSelectionError extends Error {}

/**
 * Every citation root the range touches, in document order. Outer roots are
 * kept: a § root owns the paragraph heading, and dropping it made headings
 * unmarkable. Double-storing is avoided at the traversal instead, where each
 * root contributes only the text it owns itself.
 *
 * `querySelectorAll` only reaches descendants, so the boundary roots are
 * seeded explicitly — the common ancestor is often the outer root itself.
 */
function rootsTouchedBy(range: Range, firstRoot: Element, lastRoot: Element): Element[] {
  const common = range.commonAncestorContainer
  const container = common instanceof Element ? common : common.parentElement
  const roots = new Set<Element>([firstRoot, lastRoot])

  for (const root of container?.querySelectorAll('[data-citation-source]') ?? []) {
    if (range.intersectsNode(root)) {
      roots.add(root)
    }
  }

  return [...roots].sort((a, b) =>
    a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
  )
}

/**
 * Markings may span content units, so two roots belong together when they are
 * part of the same work. Commentary gives every comment its own documentId
 * and shares a root document, so that is what identifies the work there.
 */
function workIdentityOf(root: Element): string {
  return root.getAttribute('data-marking-root-document-id')
    ?? root.getAttribute('data-marking-document-id')
    ?? ''
}

/**
 * A selection boundary can land inside noise — a `.reference` marker, a
 * footnote toggle — which has no markable offset of its own. Snap inwards to
 * the nearest markable text instead: narrowing the segment to the noise point
 * aborted the whole capture, and widening it would store more than was
 * selected. Null when this root holds no markable text on that side.
 */
function markableBoundary(
  root: Element,
  container: Node,
  offset: number,
  edge: 'start' | 'end',
): { container: Node, offset: number } | null {
  if (computeMarkableOffsetWithin(root, container, offset) >= 0) {
    return { container, offset }
  }

  const textNodes = [...iterateMarkableTextNodes(root, root)]
  const towards = edge === 'start' ? Node.DOCUMENT_POSITION_FOLLOWING : Node.DOCUMENT_POSITION_PRECEDING
  const ordered = edge === 'start' ? textNodes : textNodes.reverse()
  const node = ordered.find(candidate => container.compareDocumentPosition(candidate) & towards)

  if (!node) {
    return null
  }

  return { container: node, offset: edge === 'start' ? 0 : node.length }
}

/**
 * Splits a selection into one segment per content unit it covers, so a single
 * marking can span paragraphs, comments and book pages. Each segment anchors
 * independently at rehydration.
 */
export async function captureMarkingSelection(range: Range, headingId?: string | null) {
  const firstRoot = findCitationParent(range.startContainer)
  const lastRoot = findCitationParent(range.endContainer)

  if (!firstRoot || !lastRoot) {
    throw new MarkingSelectionError(MARKING_MESSAGES.selectionEmpty)
  }

  const contentType = firstRoot.getAttribute('data-marking-content-type')
  const workIdentity = workIdentityOf(firstRoot)
  const roots = firstRoot === lastRoot
    ? [firstRoot]
    : rootsTouchedBy(range, firstRoot, lastRoot)

  // Only the work identity is enforced. A marking carries one pageUrl, one
  // citation and one workspace card, so spanning two works cannot be stored
  // coherently. Where the selection starts and ends within that work is left
  // open on purpose.
  if (roots.some(root =>
    root.getAttribute('data-marking-content-type') !== contentType
    || workIdentityOf(root) !== workIdentity,
  )) {
    throw new MarkingSelectionError(MARKING_MESSAGES.selectionAcrossWorks)
  }

  if (roots.length > MARKING_SEGMENTS_MAX) {
    throw new MarkingSelectionError(MARKING_MESSAGES.selectionTooLong)
  }

  const selections = roots.flatMap((root) => {
    // Whitespace between child roots is owned by the outer root too. Bounding
    // a segment on it would stretch the range across a nested root and out of
    // the unit it belongs to, so only nodes with content can bound one.
    const textNodes = Array.from(iterateOwnMarkableTextNodes(root)).filter(node => node.data.trim())
    const firstText = textNodes[0]
    const lastText = textNodes[textNodes.length - 1]

    if (!firstText || !lastText) {
      return []
    }

    const selected = document.createRange()
    selected.setStart(firstText, 0)
    selected.setEnd(lastText, lastText.length)

    // A boundary only narrows the root that owns it; the roots between the
    // two ends contribute all of their own text.
    if (root === firstRoot && selected.compareBoundaryPoints(Range.START_TO_START, range) < 0) {
      const start = markableBoundary(root, range.startContainer, range.startOffset, 'start')

      if (!start) {
        return []
      }

      selected.setStart(start.container, start.offset)
    }

    if (root === lastRoot && selected.compareBoundaryPoints(Range.END_TO_END, range) > 0) {
      const end = markableBoundary(root, range.endContainer, range.endOffset, 'end')

      if (!end) {
        return []
      }

      selected.setEnd(end.container, end.offset)
    }

    const pageElements = [root, ...root.querySelectorAll('[data-citation-page]')]
    const pages = [...new Set(pageElements.flatMap((element) => {
      const page = element.getAttribute('data-citation-page')
      return page && selected.intersectsNode(element) ? [page] : []
    }))]
    return [{ range: selected, pages, ...captureSelectionContext(selected) }]
  }).filter(selection => selection.text.length > 0)
  const text = selections.map(selection => selection.text).join('')

  if (text.length > MARKING_TEXT_MAX) {
    throw new MarkingSelectionError(MARKING_MESSAGES.selectionTooLong)
  }

  const captured = await Promise.all(selections.map(async selection => ({
    ...selection,
    ...await captureMarkingSource(selection.range, headingId),
  })))
  const first = captured[0]
  const last = captured[captured.length - 1]

  if (!first || !last) {
    throw new MarkingSelectionError(MARKING_MESSAGES.selectionEmpty)
  }

  return {
    source: first.source,
    anchor: first.anchor,
    text,
    textBefore: first.textBefore,
    textAfter: last.textAfter,
    segments: captured.map(selection => ({
      unitId: selection.source.unitId,
      documentId: selection.source.documentId,
      pages: selection.pages,
      text: selection.text,
      textBefore: selection.textBefore,
      textAfter: selection.textAfter,
      anchor: selection.anchor,
    })),
  }
}
